import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit"
import {
   createLevelType,
   updateFacilityLevelSelectors,
   updateLevelType,
   updateLevelTypes,
} from "./facilitiesSlice"
import { ApiError, ApiResponse } from "@/api"
import {
   requestCreateLevelConfiguration,
   requestDeleteLevelConfiguration,
   requestLevelConfigurations,
   requestUpdateLevelConfiguration,
   requestUpdateLevelConfigurations,
} from "@/api/LevelConfigurationsService"
import { HttpCodes } from "@/constants/globals"
import {
   LevelConfigurationItem,
   LevelConfigurationItemMode,
} from "@/types/levelConfiguration"

interface LevelConfigurationsState {
   levelConfigurations: LevelConfigurationItem[]
   loading: boolean
   error: number | null
}

export const initialState: LevelConfigurationsState = {
   levelConfigurations: [],
   loading: false,
   error: null,
}

export const fetchLevelConfigurations = createAsyncThunk(
   "levelConfigurations/fetchLevelConfigurations",
   async (_, thunkAPI) => {
      try {
         const response: ApiResponse<LevelConfigurationItem[]> =
            await requestLevelConfigurations()
         if (response.status === HttpCodes.OK) {
            thunkAPI.dispatch(updateLevelTypes(response.data))
            thunkAPI.dispatch(updateFacilityLevelSelectors())
         }
         return response
      } catch (error) {
         if (error instanceof Error) {
            const apiError = error as unknown as ApiError
            return thunkAPI.rejectWithValue(apiError.status)
         }
         return thunkAPI.rejectWithValue(500)
      }
   }
)

export const createLevelConfiguration = createAsyncThunk(
   "levelConfigurations/createLevelConfiguration",
   async (levelConfiguration: Partial<LevelConfigurationItem>, thunkAPI) => {
      try {
         const response: ApiResponse<LevelConfigurationItem> =
            await requestCreateLevelConfiguration(levelConfiguration)
         if (response.status === HttpCodes.CREATED) {
            thunkAPI.dispatch(createLevelType(response.data))
            thunkAPI.dispatch(updateFacilityLevelSelectors())
         }
         return response
      } catch (error) {
         if (error instanceof Error) {
            const apiError = error as unknown as ApiError
            return thunkAPI.rejectWithValue(apiError.status)
         }
         return thunkAPI.rejectWithValue(500)
      }
   }
)

export const updateLevelConfiguration = createAsyncThunk(
   "levelConfigurations/updateLevelConfiguration",
   async (
      {
         levelConfigurationId,
         levelConfiguration,
      }: {
         levelConfigurationId: string
         levelConfiguration: Partial<LevelConfigurationItem>
      },
      thunkAPI
   ) => {
      try {
         const response: ApiResponse<LevelConfigurationItem> =
            await requestUpdateLevelConfiguration(
               levelConfigurationId,
               levelConfiguration
            )
         if (response.status === HttpCodes.OK) {
            thunkAPI.dispatch(updateLevelType(response.data))
            thunkAPI.dispatch(updateFacilityLevelSelectors())
         }
         return response
      } catch (error) {
         if (error instanceof Error) {
            const apiError = error as unknown as ApiError
            return thunkAPI.rejectWithValue(apiError.status)
         }
         return thunkAPI.rejectWithValue(500)
      }
   }
)

export const updateLevelConfigurations = createAsyncThunk(
   "levelConfigurations/updateLevelConfigurations",
   async (levelConfigurations: Partial<LevelConfigurationItem>[], thunkAPI) => {
      try {
         const response: ApiResponse<LevelConfigurationItem[]> =
            await requestUpdateLevelConfigurations(levelConfigurations)
         if (response.status === HttpCodes.OK) {
            thunkAPI.dispatch(updateLevelTypes(response.data))
            thunkAPI.dispatch(updateFacilityLevelSelectors())
         }
         return response
      } catch (error) {
         if (error instanceof Error) {
            const apiError = error as unknown as ApiError
            return thunkAPI.rejectWithValue(apiError.status)
         }
         return thunkAPI.rejectWithValue(500)
      }
   }
)

export const deleteLevelConfiguration = createAsyncThunk(
   "levelConfigurations/deleteLevelConfiguration",
   async (levelConfigurationId: string, thunkAPI) => {
      try {
         const response: ApiResponse<void> =
            await requestDeleteLevelConfiguration(levelConfigurationId)
         if (response.status === HttpCodes.NO_CONTENT) {
            thunkAPI.dispatch(fetchLevelConfigurations())
         }
         return response
      } catch (error) {
         if (error instanceof Error) {
            const apiError = error as unknown as ApiError
            return thunkAPI.rejectWithValue(apiError.status)
         }
         return thunkAPI.rejectWithValue(500)
      }
   }
)

const levelConfigurationsSlice = createSlice({
   name: "levelConfigurations",
   initialState,
   reducers: {
      resetLevelsConfigurations: (state) => {
         state.levelConfigurations = []
      },
   },
   extraReducers: (builder) => {
      builder
         .addCase(fetchLevelConfigurations.pending, (state) => {
            state.loading = true
            state.error = null
         })
         .addCase(
            fetchLevelConfigurations.fulfilled,
            (
               state,
               action: PayloadAction<ApiResponse<LevelConfigurationItem[]>>
            ) => {
               state.loading = false
               const sortedLevelConfigurations = [...action.payload.data]
                  .filter(
                     (config) =>
                        config.mode === LevelConfigurationItemMode.PHYSICAL
                  )
                  .sort((a, b) => a.depth - b.depth)
               state.levelConfigurations = sortedLevelConfigurations
            }
         )
         .addCase(fetchLevelConfigurations.rejected, (state, action) => {
            state.loading = false
            state.error = action.payload as number
         })
         .addCase(createLevelConfiguration.pending, (state) => {
            state.loading = true
            state.error = null
         })
         .addCase(createLevelConfiguration.fulfilled, (state, action) => {
            state.loading = false
            state.levelConfigurations.push(action.payload.data)
            state.error = null
         })
         .addCase(createLevelConfiguration.rejected, (state, action) => {
            state.loading = false
            state.error = action.payload as number
         })
         .addCase(updateLevelConfiguration.pending, (state) => {
            state.loading = true
            state.error = null
         })
         .addCase(updateLevelConfiguration.fulfilled, (state, action) => {
            const { id, name, icon } = action.payload.data
            state.levelConfigurations = state.levelConfigurations.map(
               (levelConfig) =>
                  levelConfig.id === id
                     ? {
                          ...levelConfig,
                          name: name,
                          icon: icon,
                       }
                     : levelConfig
            )
            state.loading = false
            state.error = null
         })
         .addCase(updateLevelConfiguration.rejected, (state, action) => {
            state.loading = false
            state.error = action.payload as number
         })
         .addCase(updateLevelConfigurations.pending, (state) => {
            state.loading = true
            state.error = null
         })
         .addCase(updateLevelConfigurations.fulfilled, (state, action) => {
            state.levelConfigurations = [...action.payload.data]
               .filter(
                  (config) =>
                     config.mode === LevelConfigurationItemMode.PHYSICAL
               )
               .sort((a, b) => a.depth - b.depth)
            state.loading = false
            state.error = null
         })

         .addCase(updateLevelConfigurations.rejected, (state, action) => {
            state.loading = false
            state.error = action.payload as number
         })
         .addCase(deleteLevelConfiguration.pending, (state) => {
            state.loading = true
            state.error = null
         })
         .addCase(deleteLevelConfiguration.fulfilled, (state, action) => {
            state.levelConfigurations = state.levelConfigurations.filter(
               (config) => config.id !== action.meta.arg
            )
            state.loading = false
            state.error = null
         })
         .addCase(deleteLevelConfiguration.rejected, (state, action) => {
            state.loading = false
            state.error = action.payload as number
         })
   },
})

export const { resetLevelsConfigurations } = levelConfigurationsSlice.actions

export default levelConfigurationsSlice.reducer
